import { Anchor, Card, Grid, Group, Stack, Table, Text, Title } from "@mantine/core";
import { Link, useParams } from "react-router";
import { ErrorState, LoadingState, PageHeader, PermissionDenied, StatusBadge } from "@how/ui";
import { isAuthorizationError } from "../../lib/query-client";
import { usePlatformRole } from "../auth/platform-role";
import { PlanBadge } from "../plans/PlanBadge";
import { ModulesCard } from "./ModulesCard";
import { OwnersCard } from "./OwnersCard";
import { PilotTextingCard } from "./PilotTextingCard";
import { TimeZoneCard } from "./TimeZoneCard";
import { TwilioCard } from "./TwilioCard";
import { WebsiteOfflineCard } from "./WebsiteOfflineCard";
import { useTenant } from "./queries";

export function TenantDetailPage() {
  const { tenantId = "" } = useParams();
  const query = useTenant(tenantId);
  const role = usePlatformRole();
  if (query.isPending) return <LoadingState rows={6} />;
  if (query.isError) {
    return isAuthorizationError(query.error) ? (
      <PermissionDenied />
    ) : (
      <ErrorState
        message="Could not load this tenant."
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }
  const { tenant, domains, entitlements } = query.data;
  return (
    <>
      <PageHeader
        title={tenant.name}
        description={tenant.slug}
        above={
          <Anchor component={Link} to="/tenants" size="sm">
            Back to tenants
          </Anchor>
        }
        actions={<StatusBadge domain="tenant_status" value={tenant.status} size="lg" />}
      />
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Stack gap="md">
            <Card>
              <Stack gap="xs">
                <Title order={4}>Profile</Title>
                <Group justify="space-between">
                  <Text c="dimmed" size="sm">
                    Plan
                  </Text>
                  <PlanBadge plan={tenant.plan} />
                </Group>
                <Group justify="space-between">
                  <Text c="dimmed" size="sm">
                    Country
                  </Text>
                  <Text size="sm">{tenant.country_code}</Text>
                </Group>
                <Group justify="space-between">
                  <Text c="dimmed" size="sm">
                    Created
                  </Text>
                  <Text size="sm">{new Date(tenant.created_at).toLocaleString()}</Text>
                </Group>
              </Stack>
            </Card>
            <TimeZoneCard
              tenantId={tenant.id}
              tenantName={tenant.name}
              timeZone={tenant.time_zone}
            />
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <ModulesCard
            tenantId={tenant.id}
            tenantName={tenant.name}
            timeZone={tenant.time_zone}
            entitlements={entitlements}
            plan={tenant.plan}
            canManage={role.data === "super_admin"}
          />
        </Grid.Col>
        {role.data === "super_admin" ? (
          <Grid.Col span={12}>
            <OwnersCard tenantId={tenant.id} tenantName={tenant.name} />
          </Grid.Col>
        ) : null}
        {role.data === "super_admin" ? (
          <Grid.Col span={12}>
            <TwilioCard tenantId={tenant.id} canManage />
          </Grid.Col>
        ) : null}
        {role.data === "super_admin" ? (
          <Grid.Col span={12}>
            <PilotTextingCard tenantId={tenant.id} tenantName={tenant.name} />
          </Grid.Col>
        ) : null}
        <Grid.Col span={12}>
          <WebsiteOfflineCard tenantId={tenant.id} tenantName={tenant.name} />
        </Grid.Col>
        <Grid.Col span={12}>
          <Card>
            <Title order={4} mb="xs">
              Domains
            </Title>
            <Table.ScrollContainer minWidth={560}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Hostname</Table.Th>
                    <Table.Th>Surface</Table.Th>
                    <Table.Th>Kind</Table.Th>
                    <Table.Th>Primary</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {domains.map((d) => (
                    <Table.Tr key={d.id}>
                      <Table.Td>{d.hostname}</Table.Td>
                      <Table.Td>{d.surface}</Table.Td>
                      <Table.Td>{d.kind}</Table.Td>
                      <Table.Td>{d.is_primary ? "yes" : "no"}</Table.Td>
                      <Table.Td>
                        <StatusBadge domain="domain_status" value={d.status} />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Card>
        </Grid.Col>
      </Grid>
    </>
  );
}
